import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { EditorState, LexicalEditor } from 'lexical'
import { nanoid } from 'nanoid'

import { CopyContent, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../common/copy'
import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { ReadOnlyLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { CitationNodeContext } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNodeContext'
import {
  CitationNode,
  getCitationNodesFromEditorState,
} from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { formatDate, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import {
  getEditorHtmlContent,
  getEditorStateFromHtml,
  getEditorTextContent,
  getTextContentFromState,
} from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { EmailResponseInteractionResult } from '../../../models/EmailResponse'
import { EmailMetadata, EmailMetadataResponseProcessing } from '../../../models/EmailMetadata'
import { EmailTypeKind } from '../../../models/EmailType'
import { IntegrationEmail } from '../../../models/Email'
import { AIAgentType } from '../../../models/AIAgent'
import { User } from '../../../models/User'
import { useExportConfirmation } from '../../../components/ConfirmationDialog/useExportConfirmation'
import { Button } from '../../../components/Button/Button'
import {
  clearEmailResponse,
  selectEmailResponse,
  selectEmailResponseAiAgentId,
  selectEmailResponseCitationIsLoading,
  selectEmailResponseCitations,
  selectEmailResponseEditing,
  selectEmailResponseEditingLoading,
  selectEmailResponseEmailTypeChanged,
  selectEmailResponseInteractionLoading,
  setEditingEmailResponse,
  setEmailResponse,
  setEmailResponseAiAgentId,
  setEmailResponseEditingIsLoading,
  setEmailResponseEmailTypeChanged,
  setEmailResponseExecutionLoading,
} from '../../../store/emailHub/emailResponse'
import { useExecuteAiAgentMutation, useInteractWithAiAgentMutation } from '../../../store/ai/aiAgent'
import { useCreateIntegrationEmailMutation } from '../../../store/integrations/integrations'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useUpdateEmailMetadataMutation } from '../../../store/integrations/emailMetadata'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { addMessage, clearChat } from '../../../store/streamingChat/streamingChat'
import { updateEmailMetadata } from '../../../store/emailHub/emailMetadata'
import { addEmailToThread } from '../../../store/emailHub/emailThreads'
import { selectEmailTypes } from '../../../store/emailHub/emailTypes'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'

import { AIAgentResultCitations, Citation } from '../../AIAgent/AIAgentResultCitations/AIAgentResultCitations'

import { EmailResponseProcessing } from '../Common/EmailResponseProcessing'
import { EmailTypeSelector } from '../Common/EmailTypeSelector'
import { useFetchEmailResponseTemplate } from '../hooks'
import { SendEmailConfirmationModal } from './SendEmailConfirmationModal'
import { PushToDraftAfterEditModal } from './PushToDraftAfterEditModal'
import { RerunEmailResponseModal } from './RerunEmailResponseModal'
import { isEmailResponseNoResult } from './utils'

import './EmailResponse.scss'

interface EmailResponseState {
  html: string
  editorState: EditorState
  displayId: string
}

export interface EmailResponseProps {
  emailConnector: string
  contact: User
  email: IntegrationEmail
  metadata: EmailMetadata | null
}

const NODES = [...LEXICAL_NODES, CitationNode]
const IGNORE_CITATION_CONFIG = {
  html: {
    export: new Map([[CitationNode, () => ({ element: null })]]),
  },
}

export const EmailResponse: React.FC<EmailResponseProps> = ({ emailConnector, contact, email, metadata }) => {
  const { tracked } = useTracking()

  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const isEditing = useAppSelector(selectEmailResponseEditing)
  const aiAgentId = useAppSelector(selectEmailResponseAiAgentId)
  const interactionIsLoading = useAppSelector(selectEmailResponseInteractionLoading)
  const isEmailResponseEditingLoading = useAppSelector(selectEmailResponseEditingLoading)
  const emailResponse = useAppSelector(selectEmailResponse)
  const citations = useAppSelector(selectEmailResponseCitations)
  const citationIsLoading = useAppSelector(selectEmailResponseCitationIsLoading)
  const emailTypeChanged = useAppSelector(selectEmailResponseEmailTypeChanged)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const [editedEmail, setEditedEmail] = useState<string | null>(null)
  const [isSending, setSending] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showEmailTypeChangedRerunModal, setShowEmailTypeChangedRerunModal] = useState<boolean>(false)
  const [showPushToDraftAfterEditModal, setShowPushToDraftAfterEditModal] = useState<boolean>(false)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()
  const [triggerCreateIntegrationEmail] = useCreateIntegrationEmailMutation()
  const [triggerUpdateEmailMetadata] = useUpdateEmailMetadataMutation()

  const { fetchEmailResponseTemplate } = useFetchEmailResponseTemplate()
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()

  useEffect(() => {
    if (emailTypeChanged) {
      setShowEmailTypeChangedRerunModal(true)
      dispatch(setEmailResponseEmailTypeChanged(false))
    }
  }, [dispatch, emailTypeChanged])

  const isNoResult = useMemo(() => isEmailResponseNoResult(emailResponse), [emailResponse])

  const emailResponseState = useMemo<EmailResponseState>(() => {
    const htmlContent = emailResponse?.email ?? ''
    return {
      html: htmlContent,
      editorState: getEditorStateFromHtml(htmlContent, NODES),
      displayId: nanoid(),
    }
  }, [emailResponse?.email])

  const filteredCitations = useMemo(() => {
    if (!citations.length || !emailResponseState) {
      return []
    }
    const citationNodes = getCitationNodesFromEditorState(emailResponseState.editorState)
    const nodeExistsForCitation = (citation: Citation) => citationNodes.find((e) => e.getCitationId() === citation.id)
    return citations.filter((citation) => nodeExistsForCitation(citation))
  }, [citations, emailResponseState])

  const handleEdit = () => {
    setEditedEmail(emailResponseState.html)
    dispatch(setEditingEmailResponse(true))
  }

  const handleCancel = () => {
    dispatch(setEditingEmailResponse(false))
    setEditedEmail(null)
  }

  const updateEmailMetadataResponseProcessing = async (responseProcessing: EmailMetadataResponseProcessing) => {
    try {
      const emailMetadata = await triggerUpdateEmailMetadata({
        accountId,
        emailConnector,
        externalId: email.externalId,
        payload: {
          threadId: email.threadId,
          senderEmail: email.sender.email,
          responseProcessing,
        },
        removeEmptyValues: false,
      }).unwrap()
      dispatch(updateEmailMetadata(emailMetadata))
    } catch (e) {
      console.error(e)
    }
  }

  const _handleEmailResponseEditInteraction = async (editedEmail: string) => {
    if (!aiAgentId) {
      return
    }
    dispatch(setEmailResponseEditingIsLoading(true))
    try {
      const emailResponseInteractionResult = (await triggerInteractWithAiAgent({
        accountId,
        aiAgentId,
        payload: { type: 'edit', email: editedEmail },
      }).unwrap()) as EmailResponseInteractionResult
      const { newAiAgentId } = emailResponseInteractionResult
      if (newAiAgentId) {
        dispatch(setEmailResponseAiAgentId(newAiAgentId))
      }
      const { result } = emailResponseInteractionResult
      dispatch(setEmailResponse(result))
      const { message } = result
      if (message) {
        dispatch(addMessage({ type: 'agent', message }))
      }
      return emailResponseInteractionResult
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setEmailResponseEditingIsLoading(false))
    }
  }

  const handleEmailResponseEditInteraction = tracked(
    _handleEmailResponseEditInteraction,
    {
      eventName: TrackEvent.EMAIL_RESPONSE_INTERACTED_WITH,
      eventDetails: { threadId: email.threadId, type: 'edit' },
    },
    {
      eventDetailsExtractor: (editedEmail: string) => ({
        editedEmail,
      }),
    },
  )

  const _handleEmailResponseExecution = async (responseDescription?: string) => {
    dispatch(clearChat())
    dispatch(setEmailResponseExecutionLoading(true))
    try {
      const { template, emailType } = await fetchEmailResponseTemplate(metadata)
      await updateEmailMetadataResponseProcessing({ startedAt: new Date().toISOString(), finishedAt: null })
      await triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.EMAIL_RESPONSE_V2,
          context: {
            contactId: contact.id,
            contactEmail: contact.email,
            emailId: email.externalId,
            threadId: email.threadId,
            emailConnector,
            emailTemplate: template,
            email: email.content['text/html'] ?? email.content['text/plain'] ?? '',
            assistantInstructions: emailType?.detectionConfig.aiAssistantInstructions ?? '',
            source: 'manual',
            responseDescription,
          },
        },
      })
      dispatch(clearEmailResponse())
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setEmailResponseExecutionLoading(false))
    }
  }

  const handleEmailResponseExecution = tracked(_handleEmailResponseExecution, {
    eventName: TrackEvent.EMAIL_RESPONSE_EXECUTED,
    eventDetails: { threadId: email.threadId, emailId: email.externalId },
  })

  const _handleCopy = async () => {
    const copyContent = getCopyContent()
    if (!copyContent) {
      return
    }
    await copyContentToClipboard(copyContent)
  }

  const handleCopy = tracked(_handleCopy, {
    eventName: TrackEvent.EMAIL_RESPONSE_COPIED,
    eventDetails: { contactId: contact.id },
  })

  const getCopyContent = () => {
    const lexicalState = JSON.stringify(emailResponseState.editorState)
    return {
      [TEXT_TYPE]: getTextContentFromState(lexicalState, NODES),
      [HTML_TYPE]: getEditorHtmlContent(emailResponseState.editorState, NODES, IGNORE_CITATION_CONFIG) ?? '',
    }
  }

  const copyContentToClipboard = async (copyContent: CopyContent) => {
    await writeCopyContentToClipboard(copyContent)
    dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Copied to Clipboard!'] }))
  }

  const handleChange = (editorState: EditorState) => {
    const htmlContent = getEditorHtmlContent(editorState, NODES)
    setEditedEmail(htmlContent)
  }

  const _handleSend = async ({ isDraft }: { isDraft: boolean }) => {
    const htmlBody = getEditorHtmlContent(emailResponseState.editorState, NODES, IGNORE_CITATION_CONFIG)
    setSending(true)
    try {
      await triggerCreateIntegrationEmail({
        accountId,
        connector: emailConnector,
        body: {
          inReplyTo: email.externalId,
          recipients: [email.sender],
          body: htmlBody ?? '',
          isDraft,
        },
      }).unwrap()
      const message = isDraft ? 'The email has been saved as draft' : 'The email has been sent'
      dispatch(addToast({ variant: 'success', title: 'Success', messages: [message] }))
      if (!isDraft) {
        dispatch(
          addEmailToThread({
            email: {
              externalId: nanoid(),
              threadId: email.threadId,
              createdAt: new Date().toISOString(),
              sender: { email: emailIntegration?.email ?? '', name: getUserDisplayName(currentUser) },
              recipients: [{ email: contact.email, name: getUserDisplayName(contact) }],
              ccRecipients: [],
              snippet: getEditorTextContent(emailResponseState.editorState).substring(0, 30),
              content: { 'text/html': htmlBody ?? '', 'text/plain': '' },
            },
            threadId: email.threadId,
          }),
        )
      }
      const responseProcessing = isDraft ? { lastDraftPushedAt: new Date().toISOString() } : { lastSentAt: new Date().toISOString() }
      await updateEmailMetadataResponseProcessing(responseProcessing)
    } catch (e) {
      console.error(e)
    } finally {
      setSending(false)
      setShowConfirmationModal(false)
    }
  }

  const handleSend = tracked(
    async ({ isDraft }: { isDraft: boolean }) => {
      await runMutationWithConfirmation(async () => {
        await _handleSend({ isDraft })
      })
    },
    {},
    {
      eventNameExtractor: ({ isDraft }: { isDraft: boolean }) => {
        return isDraft ? TrackEvent.EMAIL_RESPONSE_PUSHED_TO_DRAFT : TrackEvent.EMAIL_RESPONSE_SENT
      },
      eventDetailsExtractor: () => {
        return {
          connector: emailConnector,
          threadId: email.threadId,
          emailId: email.externalId,
        }
      },
    },
  )

  const handleSave = async () => {
    if (!editedEmail) {
      return
    }
    const interactionResult = await handleEmailResponseEditInteraction(editedEmail)
    if (interactionResult) {
      dispatch(setEditingEmailResponse(false))
      setEditedEmail(null)
      console.log(metadata)
      if (metadata?.responseProcessing.lastDraftPushedAt) {
        setShowPushToDraftAfterEditModal(true)
      }
    }
  }

  const handleEmailTypeChanged = () => {
    if (emailResponse) {
      dispatch(setEmailResponseEmailTypeChanged(true))
    }
  }

  if (!emailResponse || isNoResult) {
    return <EmailResponseGenerator email={email} metadata={metadata} handleEmailResponseExecution={handleEmailResponseExecution} contact={contact} />
  }

  return (
    <>
      {showConfirmationModal ? (
        <SendEmailConfirmationModal
          show={showConfirmationModal}
          onConfirm={() => handleSend({ isDraft: false })}
          onHide={() => setShowConfirmationModal(false)}
        />
      ) : null}
      {showEmailTypeChangedRerunModal ? (
        <RerunEmailResponseModal
          show={showEmailTypeChangedRerunModal}
          onConfirm={async () => await handleEmailResponseExecution()}
          onHide={() => setShowEmailTypeChangedRerunModal(false)}
        />
      ) : null}
      {showPushToDraftAfterEditModal ? (
        <PushToDraftAfterEditModal
          show={showPushToDraftAfterEditModal}
          onConfirm={async () => {
            await handleSend({ isDraft: true })
            setShowPushToDraftAfterEditModal(false)
          }}
          onHide={() => setShowPushToDraftAfterEditModal(false)}
          isLoading={isSending}
        />
      ) : null}
      <div className='d-flex flex-column gap-1 px-3 zocks-py-0.75 zocks-border-0.5 zocks-bg-color-gray-100'>
        <div className='paragraph-small'>Email type</div>
        <div className='email-response-type-selector-container'>
          <EmailTypeSelector email={email} metadata={metadata} onTypeChanged={handleEmailTypeChanged} />
        </div>
      </div>
      <div className='d-flex flex-column zocks-p-2.0 zocks-gap-3 zocks-border-0.5 email-response-main-container'>
        <CitationNodeContext.Provider value={{ citations: interactionIsLoading ? [] : filteredCitations }}>
          {isEditing ? (
            <EmailResponseResultEditor
              key={emailResponseState.displayId}
              initialEditorState={emailResponseState.editorState}
              handleChange={handleChange}
              interactionIsLoading={interactionIsLoading}
            />
          ) : (
            <EmailResponseResultDisplay key={emailResponseState.displayId} initialEditorState={emailResponseState.editorState} />
          )}
        </CitationNodeContext.Provider>
        {isEditing ? (
          <div className='d-flex gap-2 justify-content-end'>
            <Button variant='secondary' onClick={handleCancel} disabled={interactionIsLoading || isEmailResponseEditingLoading}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={!editedEmail} loading={interactionIsLoading || isEmailResponseEditingLoading}>
              Save
            </Button>
          </div>
        ) : (
          <div className='d-flex gap-2'>
            <OverlayTrigger
              trigger={citationIsLoading ? ['hover', 'focus'] : []}
              placement='top'
              overlay={
                <BootstrapTooltip className='zocks-text-gray-500'>Editing is disabled while references are being generated. Please hold on.</BootstrapTooltip>
              }
            >
              <Button className='button-medium' variant='header' onClick={handleEdit} disabled={interactionIsLoading || citationIsLoading}>
                <i className='bi bi-pencil-square' />
                Edit
              </Button>
            </OverlayTrigger>
            <Button variant='header' className='button-medium' onClick={handleCopy}>
              <i className='bi bi-copy' />
            </Button>
          </div>
        )}

        {!isEditing ? (
          <AIAgentResultCitations aiAgentType={AIAgentType.EMAIL_RESPONSE_V2} citationIsLoading={citationIsLoading} citations={filteredCitations} />
        ) : null}
      </div>
      <div className='d-flex py-2 px-3 align-items-center justify-content-between'>
        {metadata?.responseProcessing.lastDraftPushedAt ? (
          <div>
            <div className='paragraph-medium-extended'>The email has been placed in your draft folder. You can edit it at any time.</div>
            <div className='paragraph-medium-extended zocks-text-gray-500'>{formatDate(metadata.responseProcessing.lastDraftPushedAt)}</div>
          </div>
        ) : (
          <div />
        )}

        <div className='d-flex gap-2'>
          <Button
            variant='primaryGhost'
            className='button-medium'
            onClick={() => handleSend({ isDraft: true })}
            disabled={isEditing || isSending || interactionIsLoading || accountIsLoading || isEmailResponseEditingLoading}
          >
            <i className='bi bi-inbox' />
            Push to draft
          </Button>
          <Button
            variant='primary'
            className='button-medium'
            onClick={() => setShowConfirmationModal(true)}
            disabled={isEditing || isSending || interactionIsLoading || accountIsLoading || isEmailResponseEditingLoading}
          >
            <i className='bi bi-send' />
            Send
          </Button>
        </div>
      </div>
      <EmailResponseProcessing emailConnector={emailConnector} email={email} metadata={metadata} />
      <ConfirmationModal />
    </>
  )
}

interface EmailResponseResultEditorProps {
  initialEditorState?: EditorState
  handleChange: (editorState: EditorState, editor: LexicalEditor) => void
  interactionIsLoading: boolean
}

const EmailResponseResultEditor: React.FC<EmailResponseResultEditorProps> = ({ initialEditorState, handleChange, interactionIsLoading }) => {
  const editorRef = useRef<LexicalEditor>(null)

  useEffect(() => {
    if (!editorRef.current) {
      return
    }
    const isEditable = editorRef.current.isEditable()
    if (!interactionIsLoading === isEditable) {
      return
    }
    editorRef.current.setEditable(!interactionIsLoading)
  }, [interactionIsLoading])

  return (
    <WritableLexicalEditor
      ref={editorRef}
      onChange={handleChange}
      initialEditorState={initialEditorState}
      className='flex-column-reverse align-items-start border-0'
      containerClassName='w-100 email-inbox-response-editor-container'
      lexicalNodes={NODES}
      header={<RichTextToolbarPlugin className='px-2 pb-3 gap-2' disabled={interactionIsLoading} />}
    />
  )
}

interface EmailResponseResultDisplayProps {
  initialEditorState?: EditorState
}

const EmailResponseResultDisplay: React.FC<EmailResponseResultDisplayProps> = ({ initialEditorState }) => {
  return (
    <div className='d-flex flex-column gap-2 email-inbox-response-result-container h-100'>
      <div className='email-inbox-response-result-inner-container pb-1 flex-grow-1'>
        <ReadOnlyLexicalEditor initialEditorState={initialEditorState} lexicalNodes={NODES} />
      </div>
    </div>
  )
}

interface EmailResponseGeneratorProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
  handleEmailResponseExecution: (responseDescription?: string) => void
  contact: User
}

const EmailResponseGenerator: React.FC<EmailResponseGeneratorProps> = ({ email, metadata, handleEmailResponseExecution, contact }) => {
  const accountId = useAppSelector(selectAccountId)
  const emailResponse = useAppSelector(selectEmailResponse)
  const emailTypes = useAppSelector(selectEmailTypes)

  const [responseDescription, setResponseDescription] = useState<string>('')

  const { data: accountResponse } = useGetAccountQuery({ accountId })

  const emailAction = useMemo(() => {
    if (!accountResponse) {
      return null
    }
    const { emailTypeId } = metadata?.typeProcessing ?? {}
    const { emailActionsByEmailType, uncategorizedEmailAction } = accountResponse.data.config.email
    if (!emailTypeId) {
      return uncategorizedEmailAction
    }
    if (!(emailTypeId in emailActionsByEmailType)) {
      return 'doNotReply'
    }
    return emailActionsByEmailType[emailTypeId]
  }, [accountResponse, metadata])

  const emailTypeRoleIds = useMemo(() => {
    const emailTypeId = metadata?.typeProcessing?.emailTypeId
    if (!emailTypeId) {
      return null
    }
    const emailType = emailTypes.find((e) => e.id === emailTypeId)
    if (!emailType || emailType.kind !== EmailTypeKind.REPLY) {
      return null
    }
    return emailType.roleIds
  }, [emailTypes, metadata])

  const isNoResult = useMemo(() => isEmailResponseNoResult(emailResponse), [emailResponse])

  const handleResponseDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setResponseDescription(e.target.value)
  }

  const handleGenerateButtonClick = () => {
    handleEmailResponseExecution(responseDescription)
  }

  const emailActionIsDoNotReply = emailAction === 'doNotReply'

  const contactRoleIsNotInEmailTypeRoles = !!emailTypeRoleIds && !contact.roles.some((role) => emailTypeRoleIds.includes(role.id))

  const showResponseDescription = isNoResult || emailActionIsDoNotReply

  return (
    <div className='d-flex flex-column gap-4 p-3'>
      {isNoResult ? (
        <div className='d-flex flex-column gap-1'>
          <span className='paragraph-large bold'>Additional context needed due to limited insights</span>
          <span className='paragraph-medium'>
            To ensure a better response, we need additional context beyond the current insights from CRM and financial tools. Briefly describe what information
            to include.
          </span>
        </div>
      ) : emailActionIsDoNotReply ? (
        <div className='d-flex flex-column gap-1'>
          <span className='paragraph-large bold'>Automatic replies are turned off for this Email Type</span>
          <span className='paragraph-medium'>You can still create a custom response by briefly describing what you'd like to generate.</span>
        </div>
      ) : contactRoleIsNotInEmailTypeRoles ? (
        <div className='d-flex flex-column gap-1'>
          <span className='paragraph-large bold'>The sender’s Role didn’t match any expected Roles for assigning an Email Type</span>
          <span className='paragraph-medium'>You can still create a reply manually.</span>
        </div>
      ) : null}
      {showResponseDescription ? (
        <Form.Control as='textarea' value={responseDescription} onChange={handleResponseDescriptionChange} rows={4} placeholder='Describe your response...' />
      ) : null}
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
        <div className='d-flex align-items-center gap-2 email-response-type-selector-container'>
          <span className='paragraph-medium text-nowrap'>Email type:</span>
          <EmailTypeSelector email={email} metadata={metadata} />
        </div>
        <Button variant='primary' className='py-1 px-2' onClick={handleGenerateButtonClick} disabled={showResponseDescription && !responseDescription.length}>
          Generate Response
        </Button>
      </div>
    </div>
  )
}
